import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, createCommentVNode as _createCommentVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue";
const _hoisted_1 = ["disabled", "aria-expanded"];
const _hoisted_2 = { class: "truncate" };
const _hoisted_3 = ["id", "value", "name", "required", "aria-invalid", "aria-describedby", "disabled", "placeholder", "data-test"];
const _hoisted_4 = ["aria-selected", "onClick"];
const _hoisted_5 = { class: "truncate" };
const _hoisted_6 = { class: "shrink-0 text-eblack-500" };
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { coercePhoneInput, formatNationalPhoneMask, getNationalPhoneDigits, getPhoneCountryFromValue, getPhoneCountryOptions, getPhoneMaskPlaceholder, normalizePhoneCountry, tryNormalizePhoneNumber, } from "@/shared/utils/phone-number";
export default /*@__PURE__*/ _defineComponent({
    __name: 'PhoneInput',
    props: {
        modelValue: { default: "" },
        defaultCountry: { default: "CR" },
        disabled: { type: Boolean, default: false },
        required: { type: Boolean, default: false },
        invalid: { type: Boolean, default: false },
        ariaDescribedby: { default: undefined },
        id: { default: undefined },
        name: { default: undefined },
        placeholder: { default: undefined },
        dataTest: { default: undefined }
    },
    emits: ["update:modelValue", "validity-change", "blur"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const phoneInput = ref(null);
        const rootElement = ref(null);
        const pickerElement = ref(null);
        const isOpen = ref(false);
        const search = ref("");
        const suppressModelSync = ref(false);
        const pickerStyle = ref({
            left: "0px",
            maxHeight: "256px",
            top: "0px",
            width: "0px",
        });
        const countries = getPhoneCountryOptions();
        const selectedCountry = ref(getPhoneCountryFromValue(props.modelValue, props.defaultCountry));
        const displayValue = ref(formatNationalPhoneMask(props.modelValue, selectedCountry.value));
        const selectedDialCode = computed(() => countries.find((country) => country.iso2 === selectedCountry.value)
            ?.dialCode ?? "506");
        const filteredCountries = computed(() => {
            const query = search.value.toLowerCase();
            if (!query) {
                return countries;
            }
            return countries.filter((country) => country.name.toLowerCase().includes(query) ||
                country.iso2.toLowerCase().includes(query) ||
                country.dialCode.includes(query.replace(/^\+/, "")));
        });
        const containerClass = computed(() => props.invalid
            ? "border-red-400 focus-within:ring-red-400"
            : "border-gray-300 focus-within:ring-blue-400");
        const effectivePlaceholder = computed(() => props.placeholder ?? getPhoneMaskPlaceholder(selectedCountry.value));
        const pickerListStyle = computed(() => {
            const pickerMaxHeight = Number.parseFloat(String(pickerStyle.value.maxHeight ?? "256"));
            return {
                maxHeight: `${Math.max(96, pickerMaxHeight - 42)}px`,
            };
        });
        watch(() => props.modelValue, (value) => {
            if (suppressModelSync.value) {
                return;
            }
            selectedCountry.value = getPhoneCountryFromValue(value, props.defaultCountry);
            displayValue.value = formatNationalPhoneMask(value, selectedCountry.value);
        });
        watch(() => props.defaultCountry, (country) => {
            if (props.modelValue) {
                return;
            }
            selectedCountry.value = normalizePhoneCountry(country) ?? "CR";
        });
        watch([displayValue, selectedCountry, () => props.required], () => {
            nextTick(updateNativeValidity);
        }, { immediate: true });
        watch(isOpen, (open) => {
            if (open) {
                bindFloatingPickerListeners();
                nextTick(updatePickerPosition);
                return;
            }
            unbindFloatingPickerListeners();
        });
        onBeforeUnmount(() => {
            unbindFloatingPickerListeners();
        });
        let floatingPickerListenersBound = false;
        function togglePicker() {
            if (props.disabled) {
                return;
            }
            isOpen.value = !isOpen.value;
        }
        function selectCountry(country) {
            selectedCountry.value = country;
            displayValue.value = formatNationalPhoneMask(displayValue.value, selectedCountry.value);
            closePicker();
            search.value = "";
            emitCurrentValue();
            nextTick(() => phoneInput.value?.focus());
        }
        function handleInput(event) {
            const inputElement = event.target;
            const nextValue = inputElement?.value ?? displayValue.value;
            const cursor = inputElement?.selectionStart ?? nextValue.length;
            const previousDigits = getNationalPhoneDigits(displayValue.value, selectedCountry.value);
            const nextDigitsFromInput = getNationalPhoneDigits(nextValue, selectedCountry.value);
            const inputType = event.inputType;
            let nextDigits = nextDigitsFromInput;
            let digitPosition = countDigits(nextValue.slice(0, cursor));
            if (previousDigits === nextDigitsFromInput &&
                nextValue !== displayValue.value &&
                inputType === "deleteContentBackward") {
                nextDigits = removeDigitAt(previousDigits, Math.max(digitPosition - 1, 0));
                digitPosition = Math.max(digitPosition - 1, 0);
            }
            if (previousDigits === nextDigitsFromInput &&
                nextValue !== displayValue.value &&
                inputType === "deleteContentForward") {
                nextDigits = removeDigitAt(previousDigits, digitPosition);
            }
            displayValue.value = formatNationalPhoneMask(nextDigits, selectedCountry.value);
            emitCurrentValue();
            restoreCursorByDigitPosition(digitPosition);
        }
        function handleBlur() {
            displayValue.value = formatNationalPhoneMask(displayValue.value, selectedCountry.value);
            emitCurrentValue();
            emit("blur");
        }
        function emitCurrentValue() {
            const input = coercePhoneInput(displayValue.value);
            const normalized = tryNormalizePhoneNumber(displayValue.value, selectedCountry.value);
            const isValid = normalized !== undefined && (normalized !== null || !props.required);
            suppressModelSync.value = true;
            emit("update:modelValue", normalized === undefined ? input : normalized);
            emit("validity-change", isValid);
            updateNativeValidity();
            nextTick(() => {
                suppressModelSync.value = false;
            });
        }
        function updateNativeValidity() {
            const input = coercePhoneInput(displayValue.value);
            const normalized = tryNormalizePhoneNumber(displayValue.value, selectedCountry.value);
            const message = input !== null && normalized === undefined
                ? "Ingrese un teléfono internacional válido."
                : "";
            phoneInput.value?.setCustomValidity(message);
        }
        function countDigits(value) {
            return value.replace(/\D/g, "").length;
        }
        function removeDigitAt(value, index) {
            if (index < 0 || index >= value.length) {
                return value;
            }
            return value.slice(0, index) + value.slice(index + 1);
        }
        function restoreCursorByDigitPosition(digitPosition) {
            nextTick(() => {
                const inputElement = phoneInput.value;
                if (!inputElement) {
                    return;
                }
                inputElement.setSelectionRange(findCursorPositionAfterDigits(inputElement.value, digitPosition), findCursorPositionAfterDigits(inputElement.value, digitPosition));
            });
        }
        function findCursorPositionAfterDigits(value, digitPosition) {
            if (digitPosition <= 0) {
                return 0;
            }
            let digitsSeen = 0;
            for (let index = 0; index < value.length; index += 1) {
                if (/\d/.test(value[index])) {
                    digitsSeen += 1;
                }
                if (digitsSeen === digitPosition) {
                    return index + 1;
                }
            }
            return value.length;
        }
        function closePicker() {
            isOpen.value = false;
        }
        function bindFloatingPickerListeners() {
            if (floatingPickerListenersBound || typeof window === "undefined") {
                return;
            }
            window.addEventListener("resize", updatePickerPosition);
            window.addEventListener("scroll", updatePickerPosition, true);
            document.addEventListener("pointerdown", handleDocumentPointerDown, true);
            floatingPickerListenersBound = true;
        }
        function unbindFloatingPickerListeners() {
            if (!floatingPickerListenersBound || typeof window === "undefined") {
                return;
            }
            window.removeEventListener("resize", updatePickerPosition);
            window.removeEventListener("scroll", updatePickerPosition, true);
            document.removeEventListener("pointerdown", handleDocumentPointerDown, true);
            floatingPickerListenersBound = false;
        }
        function handleDocumentPointerDown(event) {
            const target = event.target;
            if (!(target instanceof Node)) {
                return;
            }
            if (rootElement.value?.contains(target) ||
                pickerElement.value?.contains(target)) {
                return;
            }
            closePicker();
        }
        function updatePickerPosition() {
            const root = rootElement.value;
            if (!root || typeof window === "undefined") {
                return;
            }
            const rect = root.getBoundingClientRect();
            const gap = 4;
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            const desiredMaxHeight = 256;
            const minimumHeight = 160;
            const pickerHeight = Math.min(pickerElement.value?.offsetHeight || desiredMaxHeight, desiredMaxHeight);
            const spaceBelow = viewportHeight - rect.bottom - gap;
            const spaceAbove = rect.top - gap;
            const openAbove = spaceBelow < pickerHeight && spaceAbove > spaceBelow;
            const availableHeight = Math.max(minimumHeight, Math.min(desiredMaxHeight, openAbove ? spaceAbove : spaceBelow));
            const width = Math.max(0, Math.min(rect.width, viewportWidth - gap * 2));
            const left = Math.min(Math.max(gap, rect.left), Math.max(gap, viewportWidth - width - gap));
            const top = openAbove
                ? Math.max(gap, rect.top - Math.min(pickerHeight, availableHeight) - gap)
                : Math.min(rect.bottom + gap, Math.max(gap, viewportHeight - availableHeight - gap));
            pickerStyle.value = {
                left: `${Math.round(left)}px`,
                maxHeight: `${Math.round(availableHeight)}px`,
                top: `${Math.round(top)}px`,
                width: `${Math.round(width)}px`,
            };
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                ref_key: "rootElement",
                ref: rootElement,
                class: "relative w-full"
            }, [
                _createElementVNode("div", {
                    class: _normalizeClass(["flex min-h-[38px] w-full overflow-hidden rounded-md border bg-white text-sm focus-within:ring-1", containerClass.value])
                }, [
                    _createElementVNode("button", {
                        type: "button",
                        class: "inline-flex min-w-[92px] items-center justify-between gap-2 border-r border-gray-200 bg-gray-50 px-3 text-left text-eblack-800",
                        disabled: __props.disabled,
                        "aria-expanded": isOpen.value,
                        "aria-haspopup": "listbox",
                        onClick: togglePicker
                    }, [
                        _createElementVNode("span", _hoisted_2, "+" + _toDisplayString(selectedDialCode.value), 1),
                        _cache[1] || (_cache[1] = _createElementVNode("span", {
                            "aria-hidden": "true",
                            class: "text-xs text-eblack-400"
                        }, "v", -1))
                    ], 8, _hoisted_1),
                    _createElementVNode("input", {
                        id: __props.id,
                        ref_key: "phoneInput",
                        ref: phoneInput,
                        value: displayValue.value,
                        name: __props.name,
                        type: "tel",
                        inputmode: "numeric",
                        autocomplete: "tel-national",
                        required: __props.required,
                        "aria-invalid": __props.invalid || undefined,
                        "aria-describedby": __props.ariaDescribedby,
                        disabled: __props.disabled,
                        placeholder: effectivePlaceholder.value,
                        "data-test": __props.dataTest,
                        class: "min-w-0 flex-1 border-0 bg-white px-3 py-2 text-sm outline-none focus:ring-0",
                        onBlur: handleBlur,
                        onInput: handleInput
                    }, null, 40, _hoisted_3)
                ], 2),
                (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
                    (isOpen.value)
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 0,
                            ref_key: "pickerElement",
                            ref: pickerElement,
                            class: "fixed z-[10000] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg",
                            style: _normalizeStyle(pickerStyle.value),
                            "data-test": "phone-country-picker"
                        }, [
                            _withDirectives(_createElementVNode("input", {
                                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((search).value = $event)),
                                type: "search",
                                class: "w-full border-0 border-b border-gray-200 px-3 py-2 text-sm focus:ring-0",
                                placeholder: "Buscar país"
                            }, null, 512), [
                                [
                                    _vModelText,
                                    search.value,
                                    void 0,
                                    { trim: true }
                                ]
                            ]),
                            _createElementVNode("ul", {
                                class: "overflow-y-auto py-1",
                                role: "listbox",
                                style: _normalizeStyle(pickerListStyle.value)
                            }, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredCountries.value, (country) => {
                                    return (_openBlock(), _createElementBlock("li", {
                                        key: country.iso2
                                    }, [
                                        _createElementVNode("button", {
                                            type: "button",
                                            class: _normalizeClass(["flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-ewhite-100", { 'bg-ewhite-100': country.iso2 === selectedCountry.value }]),
                                            role: "option",
                                            "aria-selected": country.iso2 === selectedCountry.value,
                                            onClick: ($event) => (selectCountry(country.iso2))
                                        }, [
                                            _createElementVNode("span", _hoisted_5, _toDisplayString(country.name), 1),
                                            _createElementVNode("span", _hoisted_6, "+" + _toDisplayString(country.dialCode), 1)
                                        ], 10, _hoisted_4)
                                    ]));
                                }), 128))
                            ], 4)
                        ], 4))
                        : _createCommentVNode("", true)
                ]))
            ], 512));
        };
    }
});
